import type { TFunction } from 'i18next';
import { Clock, WarningCircle } from 'iconoir-react';
import { cn } from 'lib/utils';

// Same threshold the usage bars use to turn amber.
const NEAR_LIMIT_PERCENT = 80;
// A plan counts as too big when, past half its period, less than this share of it was used.
const UNDERUSED_PERCENT = 10;
const UNDERUSED_AFTER_SHARE = 0.5;

type NoticeTone = 'error' | 'warning' | 'info';

export type TeamsUsageNoticeContent = {
  tone: NoticeTone;
  title: string;
  description: string;
};

export type TeamsUsageNoticeInput = {
  planName: string | null;
  /** Null while unknown (loading, or the person has no such limit). */
  dailyPercent: number | null;
  /** "tomorrow 10:27", or empty when the API gave no reset time. */
  dailyResetsAt: string;
  monthPercent: number | null;
  monthRenewsOn: string;
  /** How far into the billing period we are, 0 to 1. */
  periodElapsedShare: number;
  /** Null while the usage is loading or failed to load. */
  tokensInRange: number | null;
  rangeLabel: string;
  lastLogin: string | null;
  sharedCreditsUsed: number;
  hasCreditAccess: boolean;
  /** Access on, shared credits switched on and some left in the pool. */
  canUseSharedCredits: boolean;
};

/** The one message that matters most for this person, or null when all is well. */
export function pickTeamsUsageNotice(
  input: TeamsUsageNoticeInput,
  t: TFunction
): TeamsUsageNoticeContent | null {
  const { planName, dailyPercent, monthPercent } = input;

  if (!planName) {
    if (input.sharedCreditsUsed <= 0) return null;
    return {
      tone: 'info',
      title: t('TEAMS_NOTICE_SHARED_ONLY_TITLE', { defaultValue: 'Using shared credits only' }),
      description: t('TEAMS_NOTICE_SHARED_ONLY_TEXT', {
        used: Math.round(input.sharedCreditsUsed).toLocaleString(),
        defaultValue:
          'They have no plan and have used {{used}} shared credits. A plan would give them their own daily and monthly credits.',
      }),
    };
  }

  const isMonthUsedUp = monthPercent != null && monthPercent >= 100;
  const isDayUsedUp = dailyPercent != null && dailyPercent >= 100;

  if (isMonthUsedUp || isDayUsedUp) {
    const when = isMonthUsedUp
      ? input.monthRenewsOn
      : input.dailyResetsAt || t('TOMORROW', { defaultValue: 'tomorrow' });

    if (input.canUseSharedCredits) {
      return {
        tone: 'warning',
        title: t('TEAMS_NOTICE_ON_SHARED_TITLE', {
          defaultValue: 'Their plan ran out, so they are using shared credits',
        }),
        description: isMonthUsedUp
          ? t('TEAMS_NOTICE_ON_SHARED_MONTH_TEXT', {
              date: when,
              defaultValue:
                'Their plan renews on {{date}}. If this keeps happening, a bigger plan may cost less than shared credits.',
            })
          : t('TEAMS_NOTICE_ON_SHARED_DAY_TEXT', {
              when,
              defaultValue:
                'Their plan renews {{when}}. If this keeps happening, a bigger plan may cost less than shared credits.',
            }),
      };
    }

    const title = isMonthUsedUp
      ? t('TEAMS_NOTICE_MONTH_USED_TITLE', { defaultValue: "They've used this month's credits" })
      : t('TEAMS_NOTICE_DAY_USED_TITLE', { defaultValue: "They've used today's credits" });
    const description = input.hasCreditAccess
      ? t('TEAMS_NOTICE_BLOCKED_POOL_EMPTY_TEXT', {
          when,
          defaultValue:
            "They can't continue until {{when}}. Upgrade their plan or top up the shared credits so their work isn't interrupted.",
        })
      : t('TEAMS_NOTICE_BLOCKED_NO_ACCESS_TEXT', {
          when,
          defaultValue:
            "They can't continue until {{when}}. Upgrade their plan or switch on credit access so their work isn't interrupted.",
        });
    return { tone: 'error', title, description };
  }

  const highestPercent = Math.max(dailyPercent ?? 0, monthPercent ?? 0);
  if (highestPercent >= NEAR_LIMIT_PERCENT) {
    return {
      tone: 'warning',
      title: t('TEAMS_NOTICE_NEAR_LIMIT_TITLE', {
        percent: `${Math.round(highestPercent)}%`,
        defaultValue: "They've used {{percent}} of a limit",
      }),
      description: input.canUseSharedCredits
        ? t('TEAMS_NOTICE_NEAR_LIMIT_SHARED_TEXT', {
            defaultValue:
              'They move to shared credits when it runs out. Upgrade their plan if this is normal for them.',
          })
        : t('TEAMS_NOTICE_NEAR_LIMIT_STOP_TEXT', {
            defaultValue: 'They stop when it runs out. Upgrade their plan if this is normal for them.',
          }),
    };
  }

  if (input.tokensInRange === 0) {
    return {
      tone: 'info',
      title: t('TEAMS_NOTICE_SEAT_UNUSED_TITLE', { defaultValue: 'This seat is not being used' }),
      description: input.lastLogin
        ? t('TEAMS_NOTICE_SEAT_UNUSED_TEXT', {
            range: input.rangeLabel,
            date: input.lastLogin,
            plan: planName,
            defaultValue:
              'No use in {{range}}. Last login {{date}}. Remove their plan to free the {{plan}} seat for someone else.',
          })
        : t('TEAMS_NOTICE_SEAT_UNUSED_NO_LOGIN_TEXT', {
            range: input.rangeLabel,
            plan: planName,
            defaultValue:
              'No use in {{range}}. Remove their plan to free the {{plan}} seat for someone else.',
          }),
    };
  }

  const isUnderused =
    input.tokensInRange != null &&
    monthPercent != null &&
    monthPercent < UNDERUSED_PERCENT &&
    input.periodElapsedShare >= UNDERUSED_AFTER_SHARE;
  if (isUnderused) {
    return {
      tone: 'info',
      title: t('TEAMS_NOTICE_UNDERUSED_TITLE', { defaultValue: 'Their plan looks bigger than they need' }),
      description: t('TEAMS_NOTICE_UNDERUSED_TEXT', {
        percent: `${Math.round(monthPercent)}%`,
        plan: planName,
        defaultValue:
          "They've used {{percent}} of {{plan}} this month. A smaller plan may cover it and free this seat.",
      }),
    };
  }

  return null;
}

const toneClassName: Record<NoticeTone, string> = {
  error: 'bg-error-50 dark:bg-error-background',
  warning: 'bg-warning-background',
  info: 'bg-grey-100 dark:bg-muted',
};

const titleClassName: Record<NoticeTone, string> = {
  error: 'text-error-700 dark:text-error-high-emphasis',
  warning: 'text-warning-high-emphasis',
  info: 'text-grey-900 dark:text-foreground',
};

/** Figma 10827:15092 — the banner at the top of a person's usage. */
export function TeamsUsageNotice({ notice }: { notice: TeamsUsageNoticeContent }) {
  const Icon = notice.tone === 'info' ? Clock : WarningCircle;

  return (
    <div
      role={notice.tone === 'error' ? 'alert' : 'status'}
      className={cn('flex gap-3 rounded-section px-4 py-3.5', toneClassName[notice.tone])}
    >
      <Icon
        width={18}
        height={18}
        strokeWidth={1.5}
        className={cn('mt-0.5 shrink-0', titleClassName[notice.tone])}
        aria-hidden
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className={cn('text-sm font-medium leading-body', titleClassName[notice.tone])}>
          {notice.title}
        </p>
        <p className="text-sm text-grey-800 dark:text-grey-300">{notice.description}</p>
      </div>
    </div>
  );
}
